/**
 * GeoTrust Passkey Kit Service
 *
 * Implements true passkey-only Soroban auth using the GeoSmartAccount contract.
 *
 * Flow:
 *  1. REGISTER  — create WebAuthn credential → extract raw P-256 pubkey →
 *                 call AccountFactory.create_account → receive smart account address
 *  2. SIGN TX   — get Soroban auth hash (HashIdPreimage XDR) →
 *                 base64url-encode as WebAuthn challenge →
 *                 get WebAuthn assertion (DER sig → raw 64-byte) →
 *                 attach as SorobanAuthorizationEntry on the transaction →
 *                 submit (no Freighter popup!)
 *
 * The user's Stellar "address" is the smart account contract address,
 * not a G-address.  Store it in localStorage keyed by the credential ID.
 */

import {
  Networks,
  xdr,
  rpc,
} from '@stellar/stellar-sdk';
import {
  authorizeEntryWithCustomSignature,
  findAuthEntryIndexForAddress,
} from './sorobanAuthCredentials';

const FACTORY_ID       = process.env.REACT_APP_ACCOUNT_FACTORY_ID || '';
const WEBAUTHN_VERIFIER= process.env.REACT_APP_WEBAUTHN_VERIFIER_CONTRACT_ID || '';
const RPC_URL          = process.env.REACT_APP_SOROBAN_RPC_URL || 'https://soroban-testnet.stellar.org';
const NETWORK          = process.env.REACT_APP_STELLAR_NETWORK === 'mainnet' ? Networks.PUBLIC : Networks.TESTNET;
const LS_KEY           = 'geotrust_passkey_kit_accounts'; // localStorage key

// ── helpers ────────────────────────────────────────────────────────────────────

/** base64url → Uint8Array */
function fromB64u(b64u: string): Uint8Array<ArrayBuffer> {
  const b64 = b64u.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64);
  return Uint8Array.from(bin, c => c.charCodeAt(0));
}

/** Uint8Array → base64url (no padding) */
function toB64u(buf: Uint8Array): string {
  const bin = Array.from(buf, b => String.fromCharCode(b)).join('');
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

/** Uint8Array → hex */
function toHex(buf: Uint8Array): string {
  return Array.from(buf, b => b.toString(16).padStart(2, '0')).join('');
}

// P-256 curve order n (big-endian, 32 bytes).
// If s > n/2, replace s with n - s (low-S normalisation required by Soroban secp256r1_verify).
const P256_N = new Uint8Array([
  0xff, 0xff, 0xff, 0xff, 0x00, 0x00, 0x00, 0x00,
  0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff,
  0xbc, 0xe6, 0xfa, 0xad, 0xa7, 0x17, 0x9e, 0x84,
  0xf3, 0xb9, 0xca, 0xc2, 0xfc, 0x63, 0x25, 0x51,
]);
const P256_N_HALF = new Uint8Array([
  0x7f, 0xff, 0xff, 0xff, 0x80, 0x00, 0x00, 0x00,
  0x7f, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff,
  0xde, 0xf3, 0x7d, 0x56, 0xd3, 0x8b, 0xcf, 0x42,
  0x79, 0xdc, 0xe5, 0x61, 0x7e, 0x31, 0x92, 0xa8,
]);

/** Compare two 32-byte big-endian unsigned integers: -1 / 0 / +1 */
function cmp32(a: Uint8Array, b: Uint8Array): number {
  for (let i = 0; i < 32; i++) {
    if (a[i] < b[i]) return -1;
    if (a[i] > b[i]) return  1;
  }
  return 0;
}

/** Compute (n - x) mod n for a 32-byte big-endian integer x, where 0 < x < n. */
function subFromN(x: Uint8Array): Uint8Array {
  const result = new Uint8Array(32);
  let borrow = 0;
  for (let i = 31; i >= 0; i--) {
    const diff = P256_N[i] - x[i] - borrow;
    result[i] = ((diff + 256) & 0xff);
    borrow = diff < 0 ? 1 : 0;
  }
  return result;
}

/**
 * Convert DER-encoded ECDSA signature (what browsers return) to raw 64-byte (r‖s).
 * r and s are each padded to 32 bytes, and s is low-S normalised so that
 * Soroban's secp256r1_verify host function accepts the signature.
 */
function derToRaw64(der: Uint8Array): Uint8Array {
  // DER: 0x30 LEN 0x02 rLen r 0x02 sLen s
  let off = 2; // skip 0x30 + total length
  if (der[off] !== 0x02) throw new Error('Invalid DER signature: expected 0x02 for r');
  off++;
  const rLen = der[off++];
  const r = der.slice(off, off + rLen);
  off += rLen;
  if (der[off] !== 0x02) throw new Error('Invalid DER signature: expected 0x02 for s');
  off++;
  const sLen = der[off++];
  const s = der.slice(off, off + sLen);

  // Pad s to 32 bytes (it may have a leading 0x00 byte in DER)
  const sPadded = new Uint8Array(32);
  sPadded.set(s.slice(-32), 32 - Math.min(s.length, 32));

  // Low-S normalisation: if s > n/2, use n - s instead
  const sNorm = cmp32(sPadded, P256_N_HALF) > 0 ? subFromN(sPadded) : sPadded;

  const raw = new Uint8Array(64);
  raw.set(r.slice(-32), 32 - Math.min(r.length, 32));
  raw.set(sNorm, 32);
  return raw;
}

/**
 * Extract the raw uncompressed P-256 public key (65 bytes: 0x04 || X || Y)
 * from the SPKI DER-encoded buffer returned by
 * AuthenticatorAttestationResponse.getPublicKey().
 *
 * Uses SubtleCrypto (recommended by Stellar passkey-kit):
 *  - importKey('spki') parses the DER envelope correctly
 *  - exportKey('raw')  gives the 65-byte uncompressed EC point
 */
async function spkiToRawP256(spki: ArrayBuffer): Promise<Uint8Array> {
  const cryptoKey = await crypto.subtle.importKey(
    'spki',
    spki,
    { name: 'ECDSA', namedCurve: 'P-256' },
    true,       // exportable
    ['verify'], // usage
  );
  const raw = await crypto.subtle.exportKey('raw', cryptoKey);
  return new Uint8Array(raw); // 65 bytes: 0x04 || x || y
}

// ── Stored account record ──────────────────────────────────────────────────────

export interface SmartAccountRecord {
  credentialId: string;   // base64url WebAuthn credential ID
  publicKeyHex: string;   // raw P-256 pubkey as hex (65 bytes = 130 hex chars)
  accountAddress: string; // C... Stellar contract address of the SmartAccount
  walletAddress?: string; // optional linked Freighter wallet (for fallback)
  createdAt: number;
}

function loadAccounts(): SmartAccountRecord[] {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveAccounts(accounts: SmartAccountRecord[]): void {
  localStorage.setItem(LS_KEY, JSON.stringify(accounts));
}

// ── Main service class ─────────────────────────────────────────────────────────

export class PasskeyKitService {
  private readonly server: rpc.Server;

  constructor() {
    this.server = new rpc.Server(RPC_URL);
  }

  // ── Registration ─────────────────────────────────────────────────────────────

  /**
   * Full passkey registration + smart account deployment.
   *
   * @param userLabel  Display name shown in the OS passkey dialog.
   * @param walletAddress  Optional: link the smart account to a Freighter wallet.
   * @returns The new SmartAccountRecord (contains the on-chain contract address).
   */
  async register(userLabel: string, walletAddress?: string): Promise<SmartAccountRecord> {
    if (!FACTORY_ID) throw new Error('REACT_APP_ACCOUNT_FACTORY_ID not configured. Run deploy-passkey-kit.ps1 first.');

    // ── 1. Create WebAuthn credential ──────────────────────────────────────────
    const userIdBytes = crypto.getRandomValues(new Uint8Array(16));
    const challenge   = crypto.getRandomValues(new Uint8Array(32));

    const credential = await navigator.credentials.create({
      publicKey: {
        rp:      { id: window.location.hostname, name: 'GeoTrust' },
        user:    { id: userIdBytes, name: userLabel, displayName: userLabel },
        challenge,
        // Include both ES256 (-7) and RS256 (-257) per Stellar passkey-kit
        // recommendations to maximise authenticator compatibility.
        // We validate the algorithm below and require P-256 (ES256).
        pubKeyCredParams: [
          { type: 'public-key', alg: -7   }, // ES256 / P-256 (secp256r1) — required
          { type: 'public-key', alg: -257 }, // RS256 — listed for broad compat, rejected below
        ],
        authenticatorSelection: {
          // Do NOT restrict to 'platform' — allows security keys (YubiKey etc.) too
          residentKey:      'preferred',
          userVerification: 'required', // Stellar passkey-kit requires UV
        },
        attestation: 'none',
        timeout: 60_000,
      },
    }) as PublicKeyCredential | null;

    if (!credential) throw new Error('Passkey creation was cancelled or failed.');

    const response = credential.response as AuthenticatorAttestationResponse;
    const credId   = toB64u(new Uint8Array(credential.rawId));

    // ── 2. Validate algorithm — our smart contract only supports P-256 ────────
    const alg = response.getPublicKeyAlgorithm();
    if (alg !== -7) {
      throw new Error(
        `Your authenticator used algorithm ${alg} instead of ES256/P-256 (-7). ` +
        `GeoTrust smart accounts require a P-256 passkey. ` +
        `Try a different authenticator (e.g. Touch ID, Face ID, or a FIDO2 security key).`
      );
    }

    // ── 3. Extract raw P-256 public key from SPKI DER ─────────────────────────
    // getPublicKey() returns SPKI DER format, not a raw COSE map.
    // We use SubtleCrypto (recommended by Stellar passkey-kit) to parse it.
    const spki = response.getPublicKey();
    if (!spki) throw new Error('Browser did not return a public key. Use a newer browser (Chrome 108+, Safari 16+, Firefox 119+).');
    const rawPubKey = await spkiToRawP256(spki); // 65 bytes: 0x04 || x || y

    // ── 4. Call AccountFactory.create_account via the backend ─────────────────
    const accountAddress = await this._deploySmartAccount(rawPubKey);

    // ── 5. Persist locally ────────────────────────────────────────────────────
    const record: SmartAccountRecord = {
      credentialId: credId,
      publicKeyHex: toHex(rawPubKey),
      accountAddress,
      walletAddress,
      createdAt: Date.now(),
    };

    const accounts = loadAccounts();
    accounts.push(record);
    saveAccounts(accounts);

    return record;
  }

  /**
   * Deploy (or retrieve existing) smart account for a given P-256 public key.
   * Calls AccountFactory.create_account on-chain.
   */
  private async _deploySmartAccount(rawPubKey: Uint8Array): Promise<string> {
    const backendUrl = this._backendUrl();

    // Build the factory call args
    const pubKeyHex = toHex(rawPubKey);

    const resp = await fetch(`${backendUrl}/api/passkey-kit/create-account`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        publicKeyHex: pubKeyHex,
        webauthnVerifierId: WEBAUTHN_VERIFIER,
      }),
    });

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ error: resp.statusText }));
      throw new Error(`Failed to deploy smart account: ${err.error || resp.statusText}`);
    }

    const data = await resp.json();
    if (!data.success || !data.accountAddress) {
      throw new Error(data.error || 'Smart account deployment failed');
    }

    return data.accountAddress as string;
  }

  // ── Lookup ────────────────────────────────────────────────────────────────────

  /** Return all locally stored smart accounts. */
  getAccounts(): SmartAccountRecord[] {
    return loadAccounts();
  }

  /** Find a smart account by its Freighter wallet address (if linked). */
  getAccountByWallet(walletAddress: string): SmartAccountRecord | null {
    return loadAccounts().find(a => a.walletAddress === walletAddress) ?? null;
  }

  /** Find a smart account by credential ID. */
  getAccountByCredentialId(credId: string): SmartAccountRecord | null {
    return loadAccounts().find(a => a.credentialId === credId) ?? null;
  }

  // ── Transaction signing ───────────────────────────────────────────────────────

  /**
   * Sign a Soroban transaction using the passkey — NO Freighter needed.
   *
   * `tx` must already be simulated and prepared (resource limits set).
   * This method:
   *  1. Computes the Soroban auth hash for the smart account's authorization entry
   *  2. Sets it as the WebAuthn challenge
   *  3. Gets a WebAuthn assertion (passkey tap)
   *  4. Builds a WebAuthnSigData ScVal and attaches it as the auth credential
   *  5. Returns the modified transaction ready for submission
   *
   * @param tx           Prepared Soroban transaction (from rpc.prepareTransaction)
   * @param accountRecord The smart account to sign for
   * @returns The XDR string of the signed (authorized) transaction
   */
  async signTransaction(
    tx: any, // SorobanTransaction — using `any` to avoid SDK type import complexity
    accountRecord: SmartAccountRecord,
    latestLedger?: number,
  ): Promise<string> {
    const networkPassphrase = NETWORK;

    // ── Find the SorobanAuthorizationEntry for this smart account ─────────────
    const authEntries: xdr.SorobanAuthorizationEntry[] =
      tx.operations[0]?.auth ?? [];

    const entryIdx = findAuthEntryIndexForAddress(authEntries, accountRecord.accountAddress);

    if (entryIdx === -1) {
      throw new Error(
        `No auth entry found for smart account ${accountRecord.accountAddress}. ` +
        `Make sure the transaction was built with the smart account address as the user.`
      );
    }

    const entry = authEntries[entryIdx];

    // ── Choose the signatureExpirationLedger ──────────────────────────────────
    // assembleTransaction may leave signatureExpirationLedger as 0 (the XDR
    // default), which the Soroban host immediately rejects as "signature has
    // expired". We always override it to latestLedger + 500 (~42 minutes).
    // latestLedger is supplied by the backend from the simulation result.
    // authorizeEntry writes this value into the credentials and into the signed
    // payload, so the on-chain expiry check and the passkey signature stay
    // consistent by construction.
    const EXPIRATION_BUFFER = 500;
    const validUntilLedger = latestLedger && latestLedger > 0
      ? latestLedger + EXPIRATION_BUFFER
      : 0xFFFFFFFF;

    const accounts = loadAccounts();
    const record   = accounts.find(a => a.accountAddress === accountRecord.accountAddress);
    if (!record) throw new Error('Smart account not found in local storage');

    const credIdBuffer = fromB64u(record.credentialId);

    // The signing payload is built by the SDK from the entry's own credential
    // arm, so the legacy and address-bound (CAP-71) payloads are both correct.
    // This is what __check_auth verifies, and therefore what the passkey signs.
    const signedEntry = await authorizeEntryWithCustomSignature(
      entry,
      validUntilLedger,
      networkPassphrase,
      async (authPayload) => {
        // WebAuthn requires a non-shared buffer (BufferSource), while the SDK
        // types the payload as the wider Uint8Array<ArrayBufferLike>. Copying the
        // 32 bytes is the conversion at that boundary.
        const challenge = new Uint8Array(authPayload);

        // ── Get WebAuthn assertion using the auth payload as challenge ────────
        const assertion = await navigator.credentials.get({
          publicKey: {
            challenge,
            allowCredentials: [{ type: 'public-key', id: credIdBuffer }],
            userVerification: 'preferred',
            timeout: 60_000,
          },
        }) as PublicKeyCredential | null;

        if (!assertion) throw new Error('Passkey authentication was cancelled.');

        // ── Verify the browser used the correct credential ────────────────────
        // If the registered credential ID is not on the device, Chrome falls back
        // to showing a passkey picker. If the user picks a DIFFERENT passkey, the
        // stored on-chain public key won't match and ECDSA will always fail.
        const usedCredId = toB64u(new Uint8Array(assertion.rawId));
        if (usedCredId !== record.credentialId) {
          throw new Error(
            'Wrong passkey used. The browser used a different passkey than the one registered ' +
            'for this smart account. This usually means the original passkey was deleted or ' +
            'is not available on this device. Please open Settings → Passkey Kit, ' +
            'remove this account, and register a new passkey to create a fresh smart account.'
          );
        }

        const authResp = assertion.response as AuthenticatorAssertionResponse;

        // ── Convert DER signature → raw 64 bytes ─────────────────────────────
        const derSig = new Uint8Array(authResp.signature);
        const rawSig = derToRaw64(derSig);

        // ── Build WebAuthnSigData ScVal ────────────────────────────────────────
        // struct WebAuthnSigData { signature: BytesN<64>, authenticator_data: Bytes, client_data: Bytes }
        const authDataBytes   = new Uint8Array(authResp.authenticatorData);
        // Use direct Uint8Array — avoids any TextDecoder/TextEncoder round-trip encoding artifact
        const clientDataBytes = new Uint8Array(authResp.clientDataJSON);

        return xdr.ScVal.scvMap([
          new xdr.ScMapEntry({
            key: xdr.ScVal.scvSymbol('authenticator_data'),
            val: xdr.ScVal.scvBytes(authDataBytes),
          }),
          new xdr.ScMapEntry({
            key: xdr.ScVal.scvSymbol('client_data'),
            val: xdr.ScVal.scvBytes(clientDataBytes),
          }),
          new xdr.ScMapEntry({
            key: xdr.ScVal.scvSymbol('signature'),
            val: xdr.ScVal.scvBytes(rawSig),
          }),
        ]);
      },
      `Smart account ${accountRecord.accountAddress}`,
    );

    // ── Attach the signed entry ───────────────────────────────────────────────
    // SDK 17 credentials are immutable, so authorizeEntry returns a signed copy
    // rather than mutating in place. Replacing the array element is reflected by
    // tx.toXDR().
    authEntries[entryIdx] = signedEntry;

    // Return the modified transaction as XDR
    return tx.toXDR();
  }

  // ── Build a deposit transaction for a smart account ───────────────────────────

  /**
   * Build a complete, simulated, and passkey-signed deposit transaction.
   * This produces a transaction XDR that can be submitted directly —
   * no Freighter wallet required.
   *
   * @param accountRecord Smart account (the depositing user)
   * @param countryNumeric ISO 3166-1 numeric country code (e.g. 840)
   * @param assetAddress  Contract address of the asset (XLM native wrapper)
   * @param amountStroops Amount in stroops (1 XLM = 10_000_000 stroops)
   * @param vaultId       GeoVault contract ID
   */
  async buildAndSignDeposit(
    accountRecord: SmartAccountRecord,
    countryNumeric: number,
    assetAddress: string,
    amountStroops: number,
    vaultId: string,
  ): Promise<string> {
    // Two-phase flow — contracts (C...) cannot be Stellar transaction sources,
    // so the backend uses the service account as the fee/sequence payer.
    //
    // Phase 1: backend builds + simulates with service account as source,
    //          returns un-signed XDR with auth entries filled (nonces, expiration).
    // Phase 2 (here): frontend signs the auth entry with the passkey.
    //          The resulting XDR is returned to the caller, which then calls
    //          /api/passkey-kit/fee-pay-and-submit so the backend can add the
    //          service-account signature and submit.

    const backendUrl = this._backendUrl();

    const prepResp = await fetch(`${backendUrl}/api/passkey-kit/prepare-deposit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        smartAccountAddress: accountRecord.accountAddress,
        countryNumeric,
        assetAddress,
        amountStroops,
        vaultId,
      }),
    });

    if (!prepResp.ok) {
      const err = await prepResp.json().catch(() => ({ error: prepResp.statusText }));
      if (err.errorCode === 'INSUFFICIENT_BALANCE') {
        const has  = err.currentBalance  != null ? (err.currentBalance  / 1e7).toFixed(2) : '0';
        const need = err.requiredAmount   != null ? (err.requiredAmount   / 1e7).toFixed(2) : '?';
        throw new Error(
          `Smart account has insufficient XLM (${has} XLM, need ${need} XLM). ` +
          `Open the Passkey Kit panel and add funds to your smart account first.`
        );
      }
      throw new Error(`Failed to prepare deposit: ${err.error || prepResp.statusText}`);
    }

    const prepData = await prepResp.json();
    if (!prepData.success || !prepData.unsignedXdr) {
      throw new Error(prepData.error || 'Failed to prepare deposit transaction');
    }

    // Parse the assembled (but unsigned) transaction
    const { TransactionBuilder: TxBuilder } = await import('@stellar/stellar-sdk');
    const tx = TxBuilder.fromXDR(prepData.unsignedXdr, NETWORK);

    // Sign only the Soroban auth entry with the passkey (__check_auth).
    // Pass latestLedger so signTransaction can set a correct signatureExpirationLedger
    // (assembleTransaction may leave it as 0, which the host rejects as expired).
    return await this.signTransaction(tx, accountRecord, prepData.latestLedger);
  }

  /**
   * Generic: sign any already-assembled Soroban transaction XDR with a passkey.
   * Useful for withdraw, SAC transfers, or any other smart-account invocation
   * where the backend returned an `unsignedXdr` + `latestLedger`.
   */
  async signUnsignedXdr(
    unsignedXdr: string,
    accountRecord: SmartAccountRecord,
    latestLedger?: number,
  ): Promise<string> {
    const { TransactionBuilder: TxBuilder } = await import('@stellar/stellar-sdk');
    const tx = TxBuilder.fromXDR(unsignedXdr, NETWORK);
    return this.signTransaction(tx, accountRecord, latestLedger);
  }

  // ── Utilities ──────────────────────────────────────────────────────────────────

  private _backendUrl(): string {
    return process.env.REACT_APP_BACKEND_URL || '';
  }
}

export const passkeyKitService = new PasskeyKitService();
